
import React, { useState } from 'react';
import { LOGO } from '../constants';
import { authService } from '../services/authService';
import { Store, UserPlus, Mail, Building, ArrowRight, Loader2, CheckCircle } from 'lucide-react';

interface SellerSignupProps {
  onSignupSuccess: () => void;
}

const SellerSignup: React.FC<SellerSignupProps> = ({ onSignupSuccess }) => {
  const [email, setEmail] = useState('');
  const [storeName, setStoreName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await authService.registerSeller({ email, storeName });
      setIsSuccess(true);
      setTimeout(() => onSignupSuccess(), 2000);
    } catch (err) {
      alert("Signup failed. Try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
        <div className="text-center space-y-6 max-w-sm animate-in zoom-in duration-500">
           <div className="flex justify-center">
             <div className="bg-green-100 p-6 rounded-full text-green-600">
               <CheckCircle className="h-16 w-16" />
             </div>
           </div>
           <h1 className="text-3xl font-black text-gray-900">Store Created!</h1>
           <p className="text-gray-500">Welcome to the Lynqar family. Redirecting you to your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 grid grid-cols-1 lg:grid-cols-2">
      {/* Left: Content */}
      <div className="flex flex-col justify-center p-8 md:p-20">
        <div className="mb-12">
          {LOGO}
        </div>
        
        <div className="space-y-6 max-w-md">
          <h1 className="text-5xl font-black text-gray-900 leading-tight">Start your global business today.</h1>
          <p className="text-xl text-gray-500 leading-relaxed">Join 5,000+ sellers who switched to Lynqar for zero commission and high-speed sales.</p>
          
          <ul className="space-y-4 pt-4">
            {['Zero Commission for 3 months', 'Multi-tenant secure infrastructure', 'AI-powered product descriptions', 'Instant local & global payouts'].map((feat, i) => (
              <li key={i} className="flex items-center gap-3 text-sm font-bold text-gray-700">
                <CheckCircle className="h-5 w-5 text-blue-600" /> {feat}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Right: Form */}
      <div className="bg-white flex flex-col justify-center p-8 md:p-20 border-l border-gray-100">
        <div className="max-w-md w-full mx-auto space-y-8">
          <div className="space-y-2">
            <h2 className="text-3xl font-black text-gray-900">Become a Seller</h2>
            <p className="text-gray-400">Fill in your business details to launch your store.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-xs font-black text-gray-400 uppercase tracking-widest">Business Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-300" />
                <input 
                  required
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="name@business.com"
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all font-medium"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-black text-gray-400 uppercase tracking-widest">Store Name</label>
              <div className="relative">
                <Building className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-300" />
                <input 
                  required
                  type="text"
                  value={storeName}
                  onChange={e => setStoreName(e.target.value)}
                  placeholder="e.g. Neo Gadgets India"
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all font-medium"
                />
              </div>
            </div>

            <button 
              disabled={isLoading}
              type="submit"
              className="w-full bg-blue-600 text-white py-5 rounded-2xl font-black text-lg shadow-2xl shadow-blue-100 flex items-center justify-center gap-3 hover:bg-blue-700 active:scale-[0.98] transition-all disabled:opacity-50"
            >
              {isLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : <>Launch My Store <ArrowRight className="h-6 w-6" /></>}
            </button>
          </form>

          <p className="text-center text-xs text-gray-400 leading-relaxed">
            By clicking Launch, you agree to our Terms of Service and Privacy Policy. No credit card required.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SellerSignup;
